<template>
  <b-overlay
    variant="white"
    spinner-variant="primary"
    blur="0"
    opacity=".75"
    rounded="sm"
    :show="formLoading"
  >
    <b-card>
      <validation-observer ref="quizFormRef">
        <b-form @submit.prevent="submitForm">
          <!-- Question Text -->
          <b-row class="mb-3">
            <b-col md="6">
              <validation-provider
                #default="{ errors }"
                name="Question Text"
                rules="required"
              >
                <b-form-group
                  label="Question Text (English)"
                  label-for="question-text"
                >
                  <b-form-textarea
                    id="question-text"
                    v-model="questionForm.text"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Enter question text"
                  />
                  <b-form-invalid-feedback :state="false">
                    {{ errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col md="6">
              <b-form-group
                label="Question Text (Arabic)"
                label-for="question-text-ar"
              >
                <b-form-textarea
                  id="question-text-ar"
                  v-model="questionForm.text_ar"
                  placeholder="Enter Arabic question text"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Answers Section -->
          <div class="answers-section">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h5 class="mb-0">
                Answers
              </h5>
              <b-button variant="outline-primary" size="sm" @click="addAnswer">
                <feather-icon icon="PlusIcon" size="16" class="mr-1" />
                Add Answer
              </b-button>
            </div>

            <div
              v-for="(answer, index) in questionForm.answers"
              :key="index"
              class="answer-item mb-3 p-3 border rounded"
            >
              <div
                class="d-flex justify-content-between align-items-start mb-2"
              >
                <h6 class="mb-0">
                  Answer {{ String.fromCharCode(65 + index) }}
                </h6>
                <b-button
                  v-if="questionForm.answers.length > 2"
                  variant="outline-danger"
                  size="sm"
                  @click="removeAnswer(index)"
                >
                  <feather-icon icon="XIcon" size="14" />
                </b-button>
              </div>

              <b-row class="mb-2">
                <b-col md="6">
                  <validation-provider
                    #default="{ errors }"
                    :name="`Answer ${String.fromCharCode(65 + index)} Text`"
                    rules="required"
                  >
                    <b-form-group
                      :label="`Answer Text (English)`"
                      :label-for="`answer-text-${index}`"
                    >
                      <b-form-textarea
                        :id="`answer-text-${index}`"
                        v-model="answer.text"
                        :state="errors.length > 0 ? false : null"
                        placeholder="Enter answer text"
                      />
                      <b-form-invalid-feedback :state="false">
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>
                <b-col md="6">
                  <validation-provider
                    #default="{ errors }"
                    :name="`Answer ${String.fromCharCode(65 + index)} Text Arabic`"
                    rules="required"
                  >
                    <b-form-group
                      :label="`Answer Text (Arabic)`"
                      :label-for="`answer-text-ar-${index}`"
                    >
                      <b-form-textarea
                        :id="`answer-text-ar-${index}`"
                        v-model="answer.text_ar"
                        :state="errors.length > 0 ? false : null"
                        placeholder="Enter Arabic answer text"
                      />
                      <b-form-invalid-feedback :state="false">
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>
        
              </b-row>

              <b-row>
                <b-col md="12">
                  <b-form-checkbox
                    v-model="answer.is_correct"
                    class="custom-control-success"
                    @change="handleCorrectAnswerChange(index)"
                  >
                    <span class="font-weight-medium"
                      >This is the correct answer</span
                    >
                  </b-form-checkbox>
                </b-col>
              </b-row>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="d-flex justify-content-end mt-4">
            <b-button
              variant="outline-secondary"
              class="mr-2"
              @click="$router.push('/learning-center-quiz')"
            >
              Cancel
            </b-button>
            <b-button type="submit" variant="primary" :disabled="submitLoading">
              <b-spinner v-if="submitLoading" small class="mr-1" />
              {{ isEditMode ? "Update Question" : "Create Question" }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </b-card>
  </b-overlay>
</template>

<script>
import {
  BCard,
  BOverlay,
  BForm,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BFormInvalidFeedback,
  BButton,
  BSpinner,
  BRow,
  BCol,
  BFormTextarea,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required } from "@validations";
import useRegisterStoreModule from "@/hooks/useRegisterStoreModule";
import quizStore from "../store";
import useQuizForm from "../hooks/useQuizForm";

export default {
  components: {
    BCard,
    BOverlay,
    BForm,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BFormInvalidFeedback,
    BButton,
    BSpinner,
    BRow,
    BCol,
    ValidationProvider,
    BFormTextarea,
    ValidationObserver,
  },
  setup(props, ctx) {
    useRegisterStoreModule("learningCenterQuiz", quizStore);

    const {
      questionForm,
      formLoading,
      submitLoading,
      isEditMode,
      addAnswer,
      removeAnswer,
      submitForm,
    } = useQuizForm(props, ctx);

    const handleCorrectAnswerChange = (changedIndex) => {
      // Ensure only one answer can be marked as correct
      if (questionForm.answers[changedIndex].is_correct) {
        // If this answer is being checked, uncheck all others
        questionForm.answers.forEach((answer, index) => {
          if (index !== changedIndex) {
            answer.is_correct = false;
          }
        });
      }
    };

    return {
      questionForm,
      formLoading,
      submitLoading,
      isEditMode,
      addAnswer,
      removeAnswer,
      submitForm,
      handleCorrectAnswerChange,
      required,
    };
  },
};
</script>

<style scoped>
.answers-section {
  border-top: 1px solid #e9ecef;
  padding-top: 1rem;
}

.answer-item {
  background-color: #f8f9fa;
}

.answer-item:hover {
  background-color: #f1f3f4;
}
</style>
